// TP/Entry/Current/SL rows for active wind cycle, sorted desc by price.
// Chasing-maker state: cyan post-only row + current price only.
// Cooldown / pending-no-chase: current price only.
import type { WindSession, WindCycle } from "@/types/api";
import { fmt } from "@/lib/format";

const MAKER_FEE_RATE = 0.0002;

interface WindLevelsViewProps {
  session: WindSession;
  latestCycle: WindCycle | null | undefined;
  livePrice: number | null;
}

interface LevelRow {
  id: string;
  price: number;
  node: React.ReactNode;
}

const DOT: Record<string, string> = {
  "buy-open": "w-3 h-3 rounded-full border-2 border-success bg-success/30",
  "sell-open": "w-3 h-3 rounded-full border-2 border-danger bg-danger/30",
  "buy-filled": "w-3 h-3 rounded-full bg-success",
  "sell-filled": "w-3 h-3 rounded-full bg-danger",
  current: "w-3 h-3 rounded-full bg-warn",
};

function pctVsCurrent(price: number, current: number): string {
  const v = ((price - current) / current) * 100;
  return `${v >= 0 ? "+" : ""}${v.toFixed(2)}%`;
}

export default function WindLevelsView({
  session,
  latestCycle,
  livePrice,
}: WindLevelsViewProps) {
  const cycleStatus = latestCycle?.status;
  const hasActiveCycle =
    (cycleStatus === "open" || cycleStatus === "closing") &&
    latestCycle?.entry_price;
  const isChasing =
    cycleStatus === "pending" && latestCycle?.entry_limit_price;

  // --- Chasing-maker state ---
  if (isChasing && latestCycle?.entry_limit_price) {
    const limitPrice = latestCycle.entry_limit_price;
    const sideLabel = latestCycle.direction === "long" ? "BID" : "ASK";
    return (
      <div className="mb-4 space-y-0.5">
        {livePrice != null && livePrice > 0 && (
          <div className="flex items-center gap-2 py-0.5">
            <div className={DOT.current} />
            <span className="text-warn text-xs font-mono">
              {fmt(livePrice, session.symbol)}
            </span>
            <span className="text-warn text-[0.65rem]">current price</span>
          </div>
        )}
        <div
          className="flex items-center gap-2 py-0.5 rounded"
          style={{ background: "#06b6d410" }}
        >
          <div
            className="w-3 h-3 rounded-full border-2"
            style={{ borderColor: "#06b6d4", background: "#06b6d420" }}
          />
          <span className="text-xs font-mono" style={{ color: "#06b6d4" }}>
            {fmt(limitPrice, session.symbol)}
          </span>
          <span className="text-[0.65rem]" style={{ color: "#06b6d4" }}>
            post-only @ {sideLabel} · chasing maker fill…
          </span>
        </div>
      </div>
    );
  }

  // --- No active cycle: current price only (or empty) ---
  if (!hasActiveCycle || !latestCycle?.entry_price) {
    if (!livePrice) return null;
    return (
      <div className="mb-4 space-y-0.5">
        <div className="flex items-center gap-2 py-0.5">
          <div className={DOT.current} />
          <span className="text-warn text-xs font-mono">
            {fmt(livePrice, session.symbol)}
          </span>
          <span className="text-warn text-[0.65rem]">current price</span>
        </div>
      </div>
    );
  }

  // --- Active cycle: TP / entry / current / SL rows ---
  const entry = latestCycle.entry_price;
  const current = livePrice ?? entry;
  const activeStage = latestCycle.stage;
  const isLong = latestCycle.direction === "long";

  const tpTrigger = isLong
    ? entry * (1 + session.tp_pct / 100)
    : entry * (1 - session.tp_pct / 100);
  const slTrigger = isLong
    ? entry * (1 - session.sl_pct / 100)
    : entry * (1 + session.sl_pct / 100);
  const beMakerPrice = isLong
    ? entry * (1 + 2 * MAKER_FEE_RATE)
    : entry * (1 - 2 * MAKER_FEE_RATE);
  const beTakerPrice = isLong
    ? entry * (1 + 2 * 0.0007)
    : entry * (1 - 2 * 0.0007);

  const entryDotCls = isLong ? "buy-filled" : "sell-filled";
  const exitDotCls = isLong ? "sell-open" : "buy-open";

  // TP row depends on current stage
  let tpRow: LevelRow;
  if (activeStage === "be-taker-armed") {
    const shown = latestCycle.tp_price ?? beTakerPrice;
    tpRow = {
      id: "tp",
      price: shown,
      node: (
        <div
          className="flex items-center gap-2 py-0.5 rounded"
          style={{ background: "#a855f710" }}
        >
          <div
            className="w-3 h-3 rounded-full border-2"
            style={{ borderColor: "#a855f7", background: "#a855f720" }}
          />
          <span className="text-xs font-mono" style={{ color: "#a855f7" }}>
            {fmt(shown, session.symbol)}
          </span>
          <span className="text-[0.65rem]" style={{ color: "#a855f7" }}>
            ({pctVsCurrent(shown, current)}) TP BE
          </span>
        </div>
      ),
    };
  } else if (activeStage === "be-maker-armed") {
    const shown = latestCycle.tp_price ?? beMakerPrice;
    tpRow = {
      id: "tp",
      price: shown,
      node: (
        <div
          className="flex items-center gap-2 py-0.5 rounded"
          style={{ background: "#3b82f610" }}
        >
          <div
            className="w-3 h-3 rounded-full border-2"
            style={{ borderColor: "#3b82f6", background: "#3b82f620" }}
          />
          <span className="text-xs font-mono" style={{ color: "#3b82f6" }}>
            {fmt(shown, session.symbol)}
          </span>
          <span className="text-[0.65rem]" style={{ color: "#3b82f6" }}>
            ({pctVsCurrent(shown, current)}) TP BE
          </span>
        </div>
      ),
    };
  } else {
    const shown = latestCycle.tp_price ?? tpTrigger;
    tpRow = {
      id: "tp",
      price: shown,
      node: (
        <div className="flex items-center gap-2 py-0.5">
          <div className={DOT[exitDotCls]} />
          <span className="text-xs font-mono text-text">
            {fmt(shown, session.symbol)}
          </span>
          <span className="text-muted text-[0.65rem]">
            ({pctVsCurrent(shown, current)}) TP
          </span>
        </div>
      ),
    };
  }

  const rows: LevelRow[] = [
    tpRow,
    {
      id: "entry",
      price: entry,
      node: (
        <div className="flex items-center gap-2 py-0.5">
          <div className={DOT[entryDotCls]} />
          <span className="text-xs font-mono text-text">
            {fmt(entry, session.symbol)}
          </span>
          <span className="text-muted text-[0.65rem]">
            ({pctVsCurrent(entry, current)}) entry
          </span>
        </div>
      ),
    },
    {
      id: "current",
      price: current,
      node: (
        <div className="flex items-center gap-2 py-0.5">
          <div className={DOT.current} />
          <span className="text-warn text-xs font-mono">
            {fmt(current, session.symbol)}
          </span>
          <span className="text-warn text-[0.65rem]">current price</span>
        </div>
      ),
    },
    {
      id: "sl",
      price: slTrigger,
      node: (
        <div className="flex items-center gap-2 py-0.5">
          <div className={DOT[exitDotCls]} />
          <span className="text-xs font-mono text-text">
            {fmt(slTrigger, session.symbol)}
          </span>
          <span className="text-muted text-[0.65rem]">
            ({pctVsCurrent(slTrigger, current)}) SL
          </span>
        </div>
      ),
    },
  ];

  rows.sort((a, b) => b.price - a.price);

  return (
    <div className="mb-4 space-y-0.5">
      {rows.map((r) => (
        <div key={r.id}>{r.node}</div>
      ))}
    </div>
  );
}
